import { Transition } from '@mantine/core';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaqsImportContext } from '@/components/Faqs/hooks';
import { FaqsImportFilter } from '@/components/Faqs/import-filter';
import { FaqsImportHeader } from '@/components/Faqs/import-header';
import { FaqsImportLoading } from '@/components/Faqs/import-loading';
import { FaqsImportSheet } from '@/components/Faqs/import-sheet';
import { ImportType } from '@/components/Faqs/types';
import { useStepStore, useStepTransition } from '@/hooks/useStepStore';

const importSheetSteps = [
  {
    type: 'import-sheet',
    step: 2,
    component: FaqsImportSheet,
  },
  {
    type: 'import-sheet',
    step: 3,
    component: FaqsImportLoading,
    canBack: false,
  },
  {
    type: 'import-sheet',
    step: 4,
    component: FaqsImportFilter,
    canBack: false,
    showHeader: false,
  },
];

const steps = [...importSheetSteps];

export interface FaqsImportProps extends FaqsImportContext {}

export const FaqsImport = ({ mode }: Omit<FaqsImportProps, 'goHome'>) => {
  // 暂时仅支持单个数据源格式,支持XLSX模板导入
  const [type, setType] = useState<ImportType | null>('import-sheet');
  const { resetStep, step } = useStepStore();
  const { t } = useTranslation();
  const { endTransition, resetTransition, startTransition, transition } = useStepTransition();

  useEffect(() => {
    return () => {
      resetTransition();
    };
  }, [resetTransition]);

  const showHeader = useMemo(() => {
    const item = steps.find((item) => item.type === type && item.step === step);
    return item?.showHeader !== false;
  }, [step, type]);

  const showDescription = useMemo(() => {
    const item = steps.find((item) => item.type === type && item.step === step);
    return item?.showDescription !== false;
  }, [step, type]);

  const goHome = useCallback(() => {
    startTransition(() => {
      resetStep();
      setType('import-sheet');
    });
  }, [resetStep, startTransition]);

  return (
    <FaqsImportContext.Provider value={{ mode, goHome }}>
      {showHeader && <FaqsImportHeader showDescription={showDescription} />}
      {steps.map((item, index) => (
        <Transition
          key={`${item.type}-${index}`}
          mounted={!transition && type === item.type && item.step === step}
          transition="fade"
          duration={150}
          onExited={endTransition}
          timingFunction="ease"
        >
          {(styles) => <div style={styles}>{<item.component />}</div>}
        </Transition>
      ))}
    </FaqsImportContext.Provider>
  );
};
